// The platform copy of MK (spec section 6.8): the raw 32 bytes, base64, in a
// small versioned JSON bound to the member id and MK's keyId, placed where the
// platform will carry it to a restored or replaced phone. Both native adapters
// store this exact string; this module is what they and the web build share.
// The member id inside the copy is checked against the member reading it, so
// a copy that ends up under the wrong key (a merged restore, a mis-grouped
// preferences file) is refused rather than adopted as another member's key.
import { base64ToBytes, bytesToBase64 } from './encoding';
import { KEY_ID_PATTERN, assertKeyId, assertMemberId } from './identifiers';
import { MASTER_KEY_BYTES, assertMasterKey } from './master-key';

export const PLATFORM_COPY_VERSION = 1;
/** One item per member id under this prefix, on both platforms: the copy hydration reads. */
export const PLATFORM_COPY_KEY_PREFIX = 'courses-mk:';
/**
 * The pre-enrolment copy (spec section 6.2, decision 40), under its own
 * prefix. Hydration never reads it.
 *
 * WHY A SECOND SLOT RATHER THAN A GUARD. On iOS the platform copy is one
 * synchronised Keychain item per member id, so anything written under
 * `courses-mk:<memberId>` reaches every phone on that iCloud account within
 * seconds — where no local record exists to say the key is provisional. A
 * guard that reads a device-local store cannot defend a replicated medium. Two
 * slots put the guard in the data: an attempt that may not commit is written
 * here, and the ordinary slot is written only after the server has committed.
 * A build that predates this slot never asks for the key, so the two can ship
 * in either order.
 */
export const PROVISIONAL_COPY_KEY_PREFIX = 'courses-mk-attempt:';
/**
 * The pending generation of a rotation this device began (spec 6.12 step 1:
 * "write MK2 to the platform copy next to MK1"). Its own slot, so a restored
 * phone that finds a rotation pending on the server holds MK2 as well as MK1
 * and can finish or abort it, instead of only the recovery code reaching it.
 * Written at begin, cleared at promote and at drop.
 */
export const PENDING_COPY_KEY_PREFIX = 'courses-mk-pending:';
/** The key an eligibility probe writes and removes; a member id is numeric, so it cannot collide. */
export const PLATFORM_COPY_PROBE_KEY = `${PLATFORM_COPY_KEY_PREFIX}probe`;

/** Which of the member's three slots an adapter call addresses. */
export const PLATFORM_COPY_SLOT = Object.freeze({
  /** The copy hydration reads; only ever written for a bundle the server holds. */
  COMMITTED: 'committed',
  /** The copy an enrolment attempt writes before its POST. */
  PROVISIONAL: 'provisional',
  /** MK2 of a rotation this device began, from `rotate/begin` until it is promoted or dropped. */
  PENDING: 'pending',
});

const SLOT_PREFIXES = Object.freeze({
  [PLATFORM_COPY_SLOT.COMMITTED]: PLATFORM_COPY_KEY_PREFIX,
  [PLATFORM_COPY_SLOT.PROVISIONAL]: PROVISIONAL_COPY_KEY_PREFIX,
  [PLATFORM_COPY_SLOT.PENDING]: PENDING_COPY_KEY_PREFIX,
});

/**
 * @param {number} memberId
 * @param {string} [slot] one of PLATFORM_COPY_SLOT; the committed slot by default
 * @returns {string} the key the adapter stores that member's copy under
 */
export function platformCopyKey(memberId, slot = PLATFORM_COPY_SLOT.COMMITTED) {
  assertMemberId(memberId);
  const prefix = SLOT_PREFIXES[slot];
  if (!prefix) throw new TypeError(`Unknown platform copy slot "${slot}"`);
  return `${prefix}${memberId}`;
}

/**
 * @param {{ memberId: number, keyId: string, mk: Uint8Array }} keys
 * @returns {string} what an adapter stores for that member
 */
export function serialisePlatformCopy({ memberId, keyId, mk }) {
  assertMemberId(memberId);
  assertKeyId(keyId);
  assertMasterKey(mk);
  return JSON.stringify({ v: PLATFORM_COPY_VERSION, memberId, keyId, mk: bytesToBase64(mk) });
}

/**
 * What is written OVER the copy when this device is forgetting a key that opens
 * nothing (`courses-encryption/reset.js`).
 *
 * It carries no `keyId` and no `mk`, so `parsePlatformCopy` below answers null
 * for it on EVERY build that has ever shipped this format — the `keyId` check
 * is older than this function. That is the whole design: a slot holding this is
 * a slot that hydrates to no key, with no version gate and no coordinated
 * release, so the delete that follows is free to fail.
 *
 * @param {number} memberId
 * @returns {string} what an adapter stores in place of that member's copy
 */
export function serialisePlatformTombstone(memberId) {
  assertMemberId(memberId);
  return JSON.stringify({ v: PLATFORM_COPY_VERSION, memberId, tombstone: true });
}

/**
 * Whether a slot holds the marker above rather than a key.
 *
 * NOTHING DEPENDS ON THIS. `parsePlatformCopy` already refuses a tombstone for
 * the reason it refuses any copy without a `keyId`; this exists so that a
 * reader — and a test — can say which of the two "no key here" cases they are
 * looking at, instead of inferring it from a null.
 *
 * @param {unknown} copy what an adapter returned
 * @returns {boolean}
 */
export function isPlatformTombstone(copy) {
  if (typeof copy !== 'string' || !copy) return false;
  try {
    const parsed = JSON.parse(copy);
    return !!parsed && parsed.v === PLATFORM_COPY_VERSION && parsed.tombstone === true;
  } catch (error) {
    return false;
  }
}

/**
 * @param {unknown} copy what an adapter returned
 * @param {number} memberId the member reading it
 * @returns {{ keyId: string, mk: Uint8Array } | null} null when it is not a copy
 *   this app wrote for this member — a tombstone included
 */
export function parsePlatformCopy(copy, memberId) {
  assertMemberId(memberId);
  if (typeof copy !== 'string' || !copy) return null;
  try {
    const parsed = JSON.parse(copy);
    if (!parsed || parsed.v !== PLATFORM_COPY_VERSION) return null;
    if (parsed.memberId !== memberId) return null;
    if (typeof parsed.keyId !== 'string' || !KEY_ID_PATTERN.test(parsed.keyId)) return null;
    const mk = base64ToBytes(parsed.mk);
    if (mk.length !== MASTER_KEY_BYTES) return null;
    return { keyId: parsed.keyId, mk };
  } catch (error) {
    return null;
  }
}
